import React, { ReactNode, useCallback, useMemo } from "react";

import {
  getProfileTypeDescription,
  getProfileTypeTitle,
  getProfileTypesSorted,
  ProfileType,
  ProfileTypeName
} from "@platformsh/observability-graph/ContinuousProfiling";
import { useIntl } from "react-intl";

import Select from "ds/Select";

import ProfileTypeBullet from "../ProfileTypeBullet";

import * as S from "./ProfileTypeSelect.styles";

export type ProfileTypeSelectProps = {
  profileType: ProfileType | undefined;
  onChange: (profileType: ProfileType | undefined) => void;
  profileTypes: Record<string, ProfileType> | undefined;
};

type ProfileTypeOption = {
  label: ReactNode;
  value: ProfileType;
};

type Conditional<T> = T extends undefined ? undefined : ProfileTypeOption;

const createOption = <T extends ProfileType>(
  profileType: T | undefined
): Conditional<T> => {
  if (!profileType) {
    return undefined as Conditional<T>;
  }

  return {
    label: <ProfileTypeOptionLabel profileTypeName={profileType.name} />,
    value: profileType
  } as unknown as Conditional<T>;
};

const ProfileTypeOptionLabel = ({
  profileTypeName
}: {
  profileTypeName: ProfileTypeName;
}) => {
  return (
    <S.Option>
      <S.OptionTitle>
        <ProfileTypeBullet profileTypeName={profileTypeName} />
        <S.OptionText>{getProfileTypeTitle(profileTypeName)}</S.OptionText>
      </S.OptionTitle>
      <S.OptionDescription className="option-description">
        {getProfileTypeDescription(profileTypeName)}
      </S.OptionDescription>
    </S.Option>
  );
};

const customStyles = {
  menuPortal: provided => ({
    ...provided,
    width: 450
  })
};

const ProfileTypeSelect = ({
  profileType,
  onChange,
  profileTypes
}: ProfileTypeSelectProps) => {
  const intl = useIntl();

  const options: ProfileTypeOption[] = useMemo(
    () =>
      getProfileTypesSorted(Object.values(profileTypes || {})).map(
        createOption
      ),
    [profileTypes]
  );
  const handleChange = useCallback(
    (value: ProfileTypeOption | null) => {
      onChange(value ? value.value : undefined);
    },
    [onChange]
  );

  return (
    <S.Layout>
      <Select
        options={options}
        name="conprof-profile-type"
        id="conprof-profile-type"
        isMulti={false}
        onChange={handleChange}
        value={createOption(profileType)}
        styles={customStyles}
        placeholder={intl.formatMessage({
          id: "conprof.profile_type.placeholder"
        })}
        isLoading={profileTypes === undefined}
        isClearable={false}
        isSearchable={false}
      />
    </S.Layout>
  );
};

export default ProfileTypeSelect;
